import { useQueryClient } from '@tanstack/react-query';
import { useToast } from 'hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { useConfig } from 'hooks/use-config';
import { useGlobalMutation, useGlobalQuery } from 'state/query-client/hooks';

import {
    getMemories,
    deleteMemory,
} from '../services/memory-service';

export interface MemoryFilter {
    reference_chat_history: boolean;
    reference_saved_memories: boolean;
    memories: {
        id: string;
        text: string;
    }[];
    id: string;
}

/**
 * Custom hook to fetch memories.
 *
 * @param {MemoryFilter} params - The filter parameters for fetching memories.
 * @returns {Object} The query result for getting memories.
 *
 * @example

 */
export function useMemories() {

    const { configurationSettings } = useConfig();
    return useGlobalQuery({
        queryKey: ['memories'],
        queryFn: () => getMemories(configurationSettings),

    });
}

/**
 * Custom hook to fetch a single memory by ID.
 *
 * @param {string} id - The ID of the memory to fetch.
 * @returns {Object} The query result for getting the memory.
 *
 * @example
 * const { data, error, isLoading } = useMemory(memoryId);
 */


/**
 * Custom hook to delete a memory.
 *
 * @returns {Object} The mutation result for deleting a memory.
 *
 * @example
 * const { mutate: deleteMemory, isLoading, error } = useDeleteMemory();
 * deleteMemory(memoryId);
 */
export function useDeleteMemory() {
    const { configurationSettings } = useConfig();
    const queryClient = useQueryClient();
    const { toast } = useToast();
    const { t } = useTranslation();

    return useGlobalMutation({
        mutationFn: (id: string) => deleteMemory(configurationSettings, id),
        onSuccess: () => {
            // Invalidate and refetch memories list
            queryClient.invalidateQueries({ queryKey: ['memories'] });
            toast({
                title: t('Success'),
                description: t('Memory deleted successfully'),
                variant: 'default',
            });
        },
        onError: (error: any) => {
            toast({
                title: t('Error'),
                description: error?.message || t('Failed to delete memory'),
                variant: 'destructive',
            });
        },
    });
}